// A-12 — Gestion des intervenants et permissions granulaires.
// Pattern inspiré Nova-V3 ProjectPermissionsTable (cards par collaborateur,
// toggles 3 états None/Read/Write par périmètre).
//
// Permission requise : admin OU lead_counsel du dossier.
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Ban, Check, ChevronDown, Crown, Eye, FileText, Lock, Pencil, Plus, ReceiptText, Shield, SlidersHorizontal, Trash2, Users,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { supabase } from '../../lib/supabaseClient';
import { Button } from '../ui/Button';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import { CollaboratorSelect } from '../collaborators/CollaboratorSelect';
import { Spinner } from '../ui/Spinner';
import { Avatar } from '../ui/Avatar';
import { useDossierMembers, useSetLeadCounsel, dossiersKeys } from '../../hooks/queries/useDossiers';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useDossierPermissionScope } from '../../hooks/queries/useCabinetSettings';
import type { Dossier, DossierMember, DossierMemberRole } from '../../services/dossiers/dossierService';
import type { TablesUpdate, TablesInsert } from '../../types/database.types';

type PermLevel = 'none' | 'read' | 'write';

interface Perimeter {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  view: keyof DossierMember;
  edit: keyof DossierMember | null; // null = view-only (time_tracking)
}

const PERIMETERS: Perimeter[] = [
  { key: 'documents',      label: 'Documents',       icon: FileText, view: 'can_view_documents',       edit: 'can_edit_documents' },
  { key: 'internal_notes', label: 'Notes internes',  icon: Lock,     view: 'can_view_internal_notes',  edit: 'can_edit_internal_notes' },
  { key: 'billing',        label: 'Facturation',     icon: ReceiptText,  view: 'can_view_billing',         edit: 'can_edit_billing' },
  { key: 'time_tracking',  label: 'Time tracking',   icon: Eye,      view: 'can_view_time_tracking',   edit: null },
];

const ROLE_LABELS: Record<DossierMemberRole, string> = {
  lead_counsel: 'Avocat principal',
  counsel:      'Avocat intervenant',
  viewer:       'Lecteur',
};

const ROLE_BADGE: Record<DossierMemberRole, string> = {
  lead_counsel: 'bg-cime-primary/10 text-cime-primary border-cime-primary/30',
  counsel:      'bg-blue-50 text-blue-700 border-blue-200',
  viewer:       'bg-amber-50 text-amber-700 border-amber-200',
};

interface Props {
  dossier: Dossier;
  canManage: boolean;          // admin OU lead_counsel du dossier
}

export function DossierMembersTab({ dossier, canManage }: Props) {
  const { data: members, isLoading } = useDossierMembers(dossier.id);
  const { data: permScope = 'global' } = useDossierPermissionScope();
  const showMemberRights = permScope === 'per_dossier';
  const [addOpen, setAddOpen] = useState(false);

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Spinner /></div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-cime-primary" />
          <h2 className="text-lg font-semibold text-cime-text-primary">
            Équipe du dossier
          </h2>
          <span className="text-sm text-cime-text-muted">
            {members?.length ?? 0}
          </span>
        </div>
        {canManage && (
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="w-4 h-4 mr-1.5" /> Ajouter un intervenant
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(members ?? []).map((m) => (
          <MemberCard
            key={m.collaborator_id}
            dossier={dossier}
            member={m}
            canManage={canManage}
            showMemberRights={showMemberRights}
          />
        ))}
        {members?.length === 0 && (
          <p className="text-sm italic text-cime-text-muted col-span-2 text-center py-8">
            Aucun intervenant pour le moment.
          </p>
        )}
      </div>

      {addOpen && (
        <AddMemberModal
          dossierId={dossier.id}
          existingIds={(members ?? []).map((m) => m.collaborator_id)}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Carte intervenant ───────────────────────────────────────────────────────

function MemberCard({
  dossier, member, canManage, showMemberRights,
}: { dossier: Dossier; member: DossierMember; canManage: boolean; showMemberRights: boolean }) {
  const qc = useQueryClient();
  const { data: collaborators } = useCollaborators();
  const collab = collaborators?.find((c) => c.id === member.collaborator_id);
  const promoteLead = useSetLeadCounsel();
  const [permsOpen, setPermsOpen] = useState(false);

  const update = useMutation({
    mutationFn: async (patch: TablesUpdate<'dossier_members'>) => {
      const { error } = await supabase
        .from('dossier_members')
        .update(patch)
        .eq('dossier_id', member.dossier_id)
        .eq('collaborator_id', member.collaborator_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dossiersKeys.members(dossier.id) });
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('dossier_members')
        .delete()
        .eq('dossier_id', member.dossier_id)
        .eq('collaborator_id', member.collaborator_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dossiersKeys.members(dossier.id) });
    },
  });

  function setLevel(perimeter: Perimeter, level: PermLevel) {
    const patch: TablesUpdate<'dossier_members'> = {};
    (patch as Record<string, boolean>)[perimeter.view as string] = level !== 'none';
    if (perimeter.edit) {
      (patch as Record<string, boolean>)[perimeter.edit as string] = level === 'write';
    }
    update.mutate(patch);
  }

  function getLevel(perimeter: Perimeter): PermLevel {
    const canView = member[perimeter.view] as boolean;
    if (!canView) return 'none';
    if (perimeter.edit && (member[perimeter.edit] as boolean)) return 'write';
    return 'read';
  }

  const isLead = member.role === 'lead_counsel';
  const displayName = collab?.full_name || collab?.email || 'Inconnu';

  return (
    <div className="rounded-cime border border-neutral-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100 bg-neutral-50">
        <Avatar name={displayName} src={collab?.avatar_url} size="sm" />
        <div className="min-w-0 flex-1">
          <CollaboratorLink collaboratorId={member.collaborator_id} className="hover:underline">
            <p className="text-sm font-medium text-cime-text-primary truncate">
              {displayName}
            </p>
          </CollaboratorLink>
          {collab?.email && collab.full_name && (
            <p className="text-[11px] text-cime-text-muted truncate">{collab.email}</p>
          )}
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded border',
            ROLE_BADGE[member.role],
          )}
        >
          {isLead && <Shield className="w-2.5 h-2.5" />}
          {ROLE_LABELS[member.role]}
        </span>
        {canManage && !isLead && member.role === 'counsel' && (
          <button
            onClick={() => {
              if (
                window.confirm(
                  `Faire de ${displayName} l'avocat principal du dossier ? L'avocat principal actuel sera rétrogradé en avocat intervenant.`,
                )
              ) {
                promoteLead.mutate({
                  dossierId: dossier.id,
                  collaboratorId: member.collaborator_id,
                });
              }
            }}
            disabled={promoteLead.isPending}
            className="text-neutral-400 hover:text-cime-primary disabled:opacity-50"
            aria-label="Promouvoir avocat principal"
            title="Promouvoir avocat principal"
          >
            <Crown className="w-4 h-4" />
          </button>
        )}
        {canManage && !isLead && (
          <button
            onClick={() => {
              if (window.confirm(`Retirer ${displayName} du dossier ?`)) remove.mutate();
            }}
            className="text-neutral-400 hover:text-red-600"
            aria-label="Retirer"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Permissions — masqué en portée globale (rôle cabinet + appartenance). */}
      {showMemberRights && (isLead ? (
        <div className="px-4 py-3">
          <p className="text-[11px] text-cime-text-muted italic">
            L'avocat principal a tous les droits — non éditables.
          </p>
        </div>
      ) : (
        <div>
          {/* En-tête accordéon "Modifier les droits" */}
          <button
            type="button"
            onClick={() => setPermsOpen((v) => !v)}
            aria-expanded={permsOpen}
            className="flex items-center justify-between w-full px-4 py-2.5 text-left hover:bg-neutral-50 transition-colors"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-cime-text-primary">
              <SlidersHorizontal className="w-3.5 h-3.5 text-cime-text-muted" />
              {canManage ? 'Modifier les droits' : 'Droits du dossier'}
            </span>
            <span className="flex items-center gap-2">
              {!permsOpen && <PermsSummary getLevel={getLevel} />}
              <ChevronDown
                className={cn(
                  'w-4 h-4 text-cime-text-muted transition-transform',
                  permsOpen && 'rotate-180',
                )}
              />
            </span>
          </button>

          {/* Contenu repliable */}
          {permsOpen && (
            <div className="px-4 pb-3 pt-1 space-y-1.5 border-t border-neutral-100">
              {PERIMETERS.map((p) => {
                const level = getLevel(p);
                return (
                  <div key={p.key} className="flex items-center justify-between gap-2 py-1">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <p.icon className={cn(
                        'w-3.5 h-3.5 shrink-0',
                        level === 'none' ? 'text-neutral-300'
                          : level === 'read' ? 'text-blue-500'
                          : 'text-emerald-500',
                      )} />
                      <span className={cn(
                        'text-sm',
                        level === 'none' ? 'text-neutral-400' : 'text-cime-text-primary',
                      )}>
                        {p.label}
                      </span>
                    </div>
                    <PermToggle
                      level={level}
                      hasEdit={p.edit !== null}
                      disabled={!canManage || update.isPending}
                      onChange={(lv) => setLevel(p, lv)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Résumé compact des droits (accordéon replié) ────────────────────────────

function PermsSummary({ getLevel }: { getLevel: (p: Perimeter) => PermLevel }) {
  const active = PERIMETERS.filter((p) => getLevel(p) !== 'none');
  if (active.length === 0) {
    return <span className="text-[11px] text-neutral-400 italic">Aucun accès</span>;
  }
  return (
    <span className="flex items-center gap-1">
      {active.map((p) => {
        const level = getLevel(p);
        return (
          <span
            key={p.key}
            title={`${p.label} — ${level === 'write' ? 'lecture + édition' : 'lecture'}`}
            className={cn(
              'inline-flex items-center justify-center w-5 h-5 rounded',
              level === 'write' ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600',
            )}
          >
            <p.icon className="w-3 h-3" />
          </span>
        );
      })}
    </span>
  );
}

// ─── Toggle 3 états ──────────────────────────────────────────────────────────

function PermToggle({
  level, hasEdit, disabled, onChange,
}: {
  level: PermLevel;
  hasEdit: boolean;
  disabled?: boolean;
  onChange: (level: PermLevel) => void;
}) {
  const states: { value: PermLevel; icon: React.ComponentType<{ className?: string }>; title: string; activeClass: string }[] = [
    { value: 'none', icon: Ban,   title: 'Aucun accès', activeClass: 'bg-neutral-200 text-neutral-700' },
    { value: 'read', icon: Eye,   title: 'Lecture',     activeClass: 'bg-blue-100 text-blue-700' },
  ];
  if (hasEdit) {
    states.push({ value: 'write', icon: Pencil, title: 'Lecture + édition', activeClass: 'bg-emerald-100 text-emerald-700' });
  }

  return (
    <div className={cn(
      'inline-flex rounded-md border border-neutral-200 overflow-hidden',
      disabled && 'opacity-50 pointer-events-none',
    )}>
      {states.map((s) => {
        const active = level === s.value;
        return (
          <button
            key={s.value}
            type="button"
            onClick={() => onChange(s.value)}
            disabled={disabled}
            title={s.title}
            className={cn(
              'px-2 py-1 transition-colors',
              active ? s.activeClass : 'bg-white text-neutral-400 hover:bg-neutral-50',
            )}
          >
            <s.icon className="w-3 h-3" />
          </button>
        );
      })}
    </div>
  );
}

// ─── Modale ajout intervenant ────────────────────────────────────────────────

function AddMemberModal({
  dossierId, existingIds, onClose,
}: { dossierId: string; existingIds: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: collaborators } = useCollaborators();
  const [selected, setSelected] = useState<string | null>(null);
  const [role, setRole] = useState<DossierMemberRole>('counsel');

  const candidates = useMemo(
    () => (collaborators ?? []).filter(
      (c) => c.is_active && !existingIds.includes(c.id),
    ),
    [collaborators, existingIds],
  );

  const add = useMutation({
    mutationFn: async () => {
      if (!selected) return;
      const payload: TablesInsert<'dossier_members'> = {
        dossier_id: dossierId,
        collaborator_id: selected,
        role,
        // Defaults sensés : viewer = read partout sauf billing ; counsel = read+edit standard ; lead_counsel ne devrait pas être ajouté ici
        can_view_documents:      true,
        can_edit_documents:      role !== 'viewer',
        can_view_internal_notes: true,
        can_edit_internal_notes: role !== 'viewer',
        can_view_billing:        false,
        can_edit_billing:        false,
        can_view_time_tracking:  true,
      };
      const { error } = await supabase.from('dossier_members').insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dossiersKeys.members(dossierId) });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-cime shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h2 className="font-semibold text-cime-text-primary">Ajouter un intervenant</h2>
        </header>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
              Collaborateur
            </label>
            {candidates.length === 0 ? (
              <p className="text-sm italic text-cime-text-muted">
                Tous les collaborateurs actifs sont déjà sur ce dossier.
              </p>
            ) : (
              <CollaboratorSelect
                options={candidates}
                value={selected ?? ''}
                onChange={(id) => setSelected(id || null)}
                placeholder="— Choisir —"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
              Rôle dans le dossier
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(['counsel', 'viewer'] as DossierMemberRole[]).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={cn(
                    'px-3 py-2 text-sm rounded-md border transition',
                    role === r
                      ? 'bg-cime-primary text-white border-cime-primary'
                      : 'bg-white border-neutral-200 hover:border-cime-primary/40',
                  )}
                >
                  {ROLE_LABELS[r]}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-cime-text-muted">
              Les permissions précises pourront être ajustées après l'ajout.
            </p>
          </div>

          {add.isError && (
            <p className="text-sm text-red-600">
              {add.error instanceof Error ? add.error.message : 'Échec de l\'ajout.'}
            </p>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <Button variant="ghost" onClick={onClose} disabled={add.isPending}>Annuler</Button>
          <Button
            onClick={() => add.mutate()}
            disabled={!selected || add.isPending}
          >
            <Check className="w-4 h-4 mr-1" /> {add.isPending ? 'Ajout…' : 'Ajouter'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
